import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";
import { normalizeLocalDate } from "@/lib/date-utils";

export type ContaReceber = {
  id: string;
  user_id: string;
  paciente_id?: string;
  descricao: string;
  categoria: string;
  valor: number;
  data_vencimento: string;
  data_recebimento?: string;
  status: "Pendente" | "Recebida" | "Vencida" | "Cancelada";
  forma_pagamento?: string;
  taxa_operadora_percentual?: number;
  valor_taxa_operadora?: number;
  valor_liquido?: number;
  observacoes?: string;
  created_at: string;
  updated_at: string;
};

const normalizeContaReceber = <T extends Partial<ContaReceber>>(data: T): T => ({
  ...data,
  ...(data.data_vencimento ? { data_vencimento: normalizeLocalDate(data.data_vencimento) } : {}),
  ...("data_recebimento" in data ? { data_recebimento: normalizeLocalDate(data.data_recebimento) } : {}),
});

export function useContasReceber() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const activeClinicaId = useActiveClinicaId();

  const { data: contasReceber = [], isLoading } = useQuery({
    queryKey: ["contas_receber", activeClinicaId],
    queryFn: async () => {
      const data = await apiList<ContaReceber>("contas_receber");
      return data.map(normalizeContaReceber).sort((a, b) => String(b.data_vencimento).localeCompare(String(a.data_vencimento)));
    },
    enabled: !!user,
  });

  const createMutation = useMutation({
    mutationFn: async (data: Omit<ContaReceber, "id" | "user_id" | "created_at" | "updated_at">) => {
      return apiCreate<ContaReceber>("contas_receber", { ...normalizeContaReceber(data), user_id: user?.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contas_receber"] });
      toast({
        title: "Sucesso",
        description: "Conta a receber cadastrada com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: "Erro ao cadastrar conta a receber: " + error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: Partial<ContaReceber> & { id: string }) => {
      return apiUpdate<ContaReceber>("contas_receber", id, normalizeContaReceber(data));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contas_receber"] });
      toast({
        title: "Sucesso",
        description: "Conta a receber atualizada com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: "Erro ao atualizar conta a receber: " + error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiDelete("contas_receber", id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contas_receber"] });
      toast({
        title: "Sucesso",
        description: "Conta a receber excluída com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: "Erro ao excluir conta a receber: " + error.message,
        variant: "destructive",
      });
    },
  });

  return {
    contasReceber,
    isLoading,
    createConta: createMutation.mutateAsync,
    updateConta: updateMutation.mutateAsync,
    deleteConta: deleteMutation.mutateAsync,
  };
}
